import { getProductivityDetails } from '../calculations';
import { CHART_COLORS } from '../constants';
import { formatCurrency, formatCurrencyDecimal, formatNumber } from '../formatters';
import { type CurrencyConfig, type CurrencyKey, type RoiResults, type SliderInput } from '../types';
import DataTable, { type DataRow } from './DataTable';
import DonutChart from './DonutChart';
import HorizontalBarChart from './HorizontalBarChart';
import styles from './Report.module.scss';

interface ProductivitySectionProps {
  results: RoiResults;
  inputs: SliderInput;
  currencyConfig: CurrencyConfig;
  currency: CurrencyKey;
}

export default function ProductivitySection({
  results,
  inputs,
  currencyConfig,
  currency,
}: ProductivitySectionProps) {
  const details = getProductivityDetails(inputs, currencyConfig);

  const yearsData = [
    { name: 'Year 1', value: results.productivity.total.year1, color: CHART_COLORS.plum },
    { name: 'Year 2', value: results.productivity.total.year2, color: CHART_COLORS.plumLight },
    { name: 'Year 3', value: results.productivity.total.year3, color: CHART_COLORS.orangeDark },
  ];

  const splitData = [
    { name: 'Desktop Workers', value: details.desktop.adjustedTotal, color: CHART_COLORS.plum },
    {
      name: 'Frontline Workers',
      value: details.frontline.adjustedTotal,
      color: CHART_COLORS.orangeDark,
    },
  ];

  const desktopRows: DataRow[] = [
    {
      label: 'Number of desktop workers',
      calculation: 'Includes predicted growth in years 2 and 3',
      values: [...details.desktop.employees.map(formatNumber), ''],
    },
    {
      label: '% of active users',
      calculation:
        'Year 1 includes time to onboard & project time. Years 2/3 show intranet maturity',
      values: [...details.desktop.activePercent.map((p) => `${p}%`), ''],
    },
    {
      label: 'Number of active users',
      calculation: 'Number of workers * % of active users',
      values: [...details.desktop.activeUsers.map(formatNumber), ''],
    },
    {
      label: 'Hourly rate',
      calculation: 'Average salary/working hours per year (includes 3% rise per year)',
      values: [...details.desktop.hourlyRate.map((v) => formatCurrencyDecimal(v, currency)), ''],
    },
    {
      label: 'Average % of time saved p/wk finding information',
      calculation: "Interact's expected average saving",
      values: [
        `${details.desktop.expectedSaving}%`,
        `${details.desktop.expectedSaving}%`,
        `${details.desktop.expectedSaving}%`,
        '',
      ],
    },
    {
      label: 'Average number of hours saved per year',
      calculation:
        "Working hours p/w * Interact's expected saving * working weeks a year * No of active users",
      values: [...details.desktop.hoursSaved.map(formatNumber), ''],
    },
    {
      label: 'Hours captured per year',
      calculation: 'Average number of hours saved p/y * % of time captured',
      values: [...details.desktop.hoursCaptured.map(formatNumber), ''],
    },
    {
      label: 'Value of time captured',
      calculation: 'Hours captured * Hourly rate',
      values: [...details.desktop.valueOfTimeCaptured.map((v) => formatCurrency(v, currency)), ''],
    },
    {
      label: 'Adjusted for unforeseeable circumstances',
      calculation: 'Catering in risk for unpredicted business circumstances (35%)',
      values: [
        ...details.desktop.adjustedValue.map((v) => formatCurrency(v, currency)),
        formatCurrency(details.desktop.adjustedTotal, currency),
      ],
    },
  ];

  const frontlineRows: DataRow[] = [
    {
      label: 'Number of frontline workers',
      calculation: 'Includes predicted growth in years 2 and 3',
      values: [...details.frontline.employees.map(formatNumber), ''],
    },
    {
      label: '% of active users',
      calculation:
        'Year 1 includes time to onboard & project time. Years 2/3 show intranet maturity',
      values: [...details.frontline.activePercent.map((p) => `${p}%`), ''],
    },
    {
      label: 'Number of active users',
      calculation: 'Number of workers * % of active users',
      values: [...details.frontline.activeUsers.map(formatNumber), ''],
    },
    {
      label: 'Hourly rate',
      calculation: 'Average salary/working hours per year (includes 3% rise per year)',
      values: [...details.frontline.hourlyRate.map((v) => formatCurrencyDecimal(v, currency)), ''],
    },
    {
      label: 'Average % of time saved p/wk finding information',
      calculation: "Interact's expected average saving",
      values: [
        `${details.frontline.expectedSaving}%`,
        `${details.frontline.expectedSaving}%`,
        `${details.frontline.expectedSaving}%`,
        '',
      ],
    },
    {
      label: 'Average number of hours saved per year',
      calculation:
        "Working hours p/w * Interact's expected saving * working weeks a year * No of active users",
      values: [...details.frontline.hoursSaved.map(formatNumber), ''],
    },
    {
      label: 'Hours captured per year',
      calculation: 'Average number of hours saved p/y * % of time captured',
      values: [...details.frontline.hoursCaptured.map(formatNumber), ''],
    },
    {
      label: 'Value of time captured',
      calculation: 'Hours captured * Hourly rate',
      values: [
        ...details.frontline.valueOfTimeCaptured.map((v) => formatCurrency(v, currency)),
        '',
      ],
    },
    {
      label: 'Adjusted for unforeseeable circumstances',
      calculation: 'Catering in risk for unpredicted business circumstances (35%)',
      values: [
        ...details.frontline.adjustedValue.map((v) => formatCurrency(v, currency)),
        formatCurrency(details.frontline.adjustedTotal, currency),
      ],
    },
  ];

  const tableHeaders = ['Time Saved', 'Calculation', 'Year 1', 'Year 2', 'Year 3', 'Total'];

  return (
    <section className={styles.section} aria-label="Productivity Yield">
      <div className={styles.sectionInner}>
        <div className={styles.splitLayout}>
          <div>
            <h2 className={styles.sectionTitle}>Productivity Yield</h2>
            <p className={styles.bodyText}>
              One of the greatest barriers to workplace productivity is the inefficiency that
              occurrs when employees cannot easily find the information they need to do their jobs.
              Time spent searching can be frustrating, unrewarding, and ultimately result in an
              increased burden on HR and IT.
            </p>
            <p className={styles.bodyText}>
              To streamline this process, an enterprise search tool can work seamlessly inside an
              improved intranet experience. When combined with improved content governance controls,
              advanced search functionality offers long-term business efficiency savings for both
              desktop and frontline employees.
            </p>
            <p className={styles.bodyText}>
              While the intention of every user is fundamentally the same: namely, to find
              information that helps them be more productive (e.g., policies and procedures), the
              positive impact gained through improved search capabilities is not universally
              constant. Different parts of the workforce access tools and information in different
              ways, resulting in desktop workers being more reliant on intranets than frontline
              peers.
            </p>
            <p className={styles.bodyText}>
              Historical observations of desktop workers reveal an average total visit time of less
              than five minutes per day, typically split into two or three visits. For frontline
              employees though, this is much less, with some logging in only a few times a week.
              These differences in access are important for organizations to be aware of because any
              calculation of ROI depends on a customized view of how that particular organization
              works.
            </p>
            <p className={styles.bodyText}>
              Regardless of whether it is frontline, desktop, or a mixture of workers who are
              included, even a small gain in the amount of time saved per employee can have huge
              repercussions on the associated value of productivity gains. Clearly, organizations
              will not benefit from 100% of the time saved by each employee (as they may use this in
              a variety of other ways), but the business will benefit from a proportion of it, and
              the value of such gains is still significant even when this and other mitigating
              factors, including macroeconomic risks and realities, are included.
            </p>
          </div>
          <div>
            <span className={styles.eyebrow}>Over three years</span>
            <h3 className={styles.sectionValue}>
              {formatCurrency(results.productivity.total.total, currency)}
            </h3>
            <HorizontalBarChart data={yearsData} currency={currency} height={180} />
            <div className={styles.chartSpacer} />
            <DonutChart data={splitData} currency={currency} />
          </div>
        </div>

        <DataTable
          title="Desktop workers"
          headerIcon="desktop"
          headers={tableHeaders}
          rows={desktopRows}
        />
        <div className={styles.tableSpacer} />
        <DataTable
          title="Frontline workers"
          headerIcon="frontline"
          headers={tableHeaders}
          rows={frontlineRows}
        />
      </div>
    </section>
  );
}
